import { instrumentApi, pnidApi } from "../api";
import type {
  BoundingBox,
  LineType,
  PidDocument,
  PnidEquipmentUpdate,
  PnidInstrument,
  PnidNode,
  PnidWizardState,
} from "../types";
import { getInstrumentEffectiveContext } from "../utils/unifiedInstruments";
import { refForEntity } from "./entityRef";

export const getNodeLinePropertyPayload = (
  values: Record<string, any>
) => {
  const flowProfile = values.flow_profile ?? null;
  const flowDirection = values.flow_direction ?? null;

  return {
    installation_type: values.installation_type ?? null,
    is_jacketed: Boolean(values.is_jacketed),
    tracing_type: values.tracing_type ?? null,
    flow_profile: flowProfile,
    slope_ratio: flowProfile === "sloped" ? values.slope_ratio ?? null : null,
    is_two_phase: Boolean(values.is_two_phase),
    flow_direction: flowDirection,
    primary_direction:
      flowDirection === "bidirectional_primary"
        ? values.primary_direction ?? null
        : null,
  };
};

const getInstrumentLineNoToken = (value: unknown, fallback: string) => {
  const text = String(value ?? "").trim();
  return text ? text.replace(/\s+/g, "_") : fallback;
};

export const getNodeLineTypePayload = (
  values: Record<string, any>
) => {
  const lineType: LineType = values.line_type ?? "process";
  const instrumentLineSubtype =
    lineType === "instrument" ? values.instrument_line_subtype ?? null : null;
  const lineNo = String(values.line_no ?? "").trim();

  return {
    line_no:
      lineType === "instrument" && !lineNo
        ? [
            getInstrumentLineNoToken(values.source_ref, "source"),
            getInstrumentLineNoToken(values.dest_ref, "destination"),
            getInstrumentLineNoToken(instrumentLineSubtype, "instrument"),
          ].join("_")
        : lineNo,
    line_type: lineType,
    instrument_line_subtype: instrumentLineSubtype,
  };
};

/** The line fields the mark dialog saves on a stored line. */
export function buildNodeUpdatePayload(values: Parameters<typeof getNodeLineTypePayload>[0]) {
  return {
    node_no: values.node_no ?? undefined,
    source_ref: values.source_ref,
    source_pnid: values.source_pnid,
    source_id: values.source_id ?? null,
    dest_ref: values.dest_ref,
    dest_pnid: values.dest_pnid,
    dest_id: values.dest_id ?? null,
    ...getNodeLineTypePayload(values),
    note: values.note ?? null,
    painting_spec: values.painting_spec?.trim() || null,
    is_duct: Boolean(values.is_duct),
    ...(values.notes !== undefined ? { notes: values.notes || null } : {}),
    ...getNodeLinePropertyPayload(values),
    drain_type: values.drain_type ?? null,
  };
}

/** The equipment fields the mark dialog saves. The two flags say whether the dialog
 *  sent those arrays at all: an absent key leaves the server's mirrored links alone. */
export function buildEquipmentUpdatePayload(
  values: Record<string, any>,
  connectionsChanged: boolean,
  pfdLinksChanged: boolean
): PnidEquipmentUpdate {
  return {
    equipment_no: values.equipment_no,
    equipment_type: values.equipment_type,
    subtype: values.subtype ?? null,
    description: values.description,
    pnid_no: values.pnid_no ?? null,
    painting_spec: values.painting_spec?.trim() || null,
    ...(values.is_primary_source !== undefined
      ? { is_primary_source: values.is_primary_source }
      : {}),
    // Empty names the drawing's own plant, so the save states the owner every
    // time rather than leaving the server to decide. A mark whose picker the
    // reviewer never touched carries nothing yet, and means the same.
    owner_domain_id: values.owner_domain_id ?? '',
    ...(values.notes !== undefined ? { notes: values.notes || null } : {}),
    ...(connectionsChanged
      ? { connected_equipments: values.connected_equipments }
      : {}),
    ...(pfdLinksChanged ? { linked_pfds: values.linked_pfds } : {}),
  };
}

// Helper to compute bounding box from polyline points
export function polylineToBbox(points: number[], includePolygon: boolean = false): BoundingBox {
  if (points.length < 2) {
    return { x1: 0, y1: 0, x2: 0, y2: 0 };
  }
  const xs: number[] = [];
  const ys: number[] = [];
  for (let i = 0; i < points.length; i += 2) {
    xs.push(points[i]);
    ys.push(points[i + 1]);
  }
  const padding = 10;
  const bbox: BoundingBox = {
    x1: Math.min(...xs) - padding,
    y1: Math.min(...ys) - padding,
    x2: Math.max(...xs) + padding,
    y2: Math.max(...ys) + padding,
  };
  if (includePolygon) {
    bbox.polygon = [...points];
  }
  return bbox;
}

export function nodeGeometryBbox(node: PnidNode | null | undefined): BoundingBox | null {
  if (!node) return null;
  if (node.is_duct) return node.duct_bbox ?? node.label_bbox ?? null;
  return node.polyline ? polylineToBbox(node.polyline) : node.label_bbox ?? null;
}

/**
 * DECISION(2026-10-27, abhishek): An instrument bound as a line end is attached to that line on save.
 * The line wins over the equipment or instrument it hung off, and an instrument that stops being
 * an end keeps its attachment; a stale attachment after a redrawn line is the signal to revisit.
 */
export const attachEndInstrumentsToLine = async (
  line: PnidNode,
  selectedDoc: PidDocument,
  pnidState: PnidWizardState | null,
  showAlert: (title: string, message: string, type?: "success" | "error" | "info" | "warning") => void,
  refreshPnidState: (docId: string) => Promise<PnidWizardState>,
) => {
  if (!selectedDoc || !pnidState) return;
  const endInstruments = pnidState.instruments.filter((instrument) => {
    if (instrument.id !== line.source_id && instrument.id !== line.dest_id) return false;
    const context = getInstrumentEffectiveContext(instrument, pnidState);
    return !(context.context_type === 'node' && context.context_id === line.id);
  });
  if (!endInstruments.length) return;
  const attachedInstruments: PnidInstrument[] = [];
  try {
    // One write at a time: each update loads, changes and saves the whole drawing on the
    // backend, so two at once can overwrite each other.
    for (const instrument of endInstruments) {
      attachedInstruments.push(await instrumentApi.updateInstrument(selectedDoc.id, instrument.id, {
        context_type: 'node',
        context_id: line.id,
      }));
    }
  } catch (error) {
    console.error('Failed to attach end instruments to the line:', error);
    showAlert('Attach failed', 'The line saved, but an instrument at its end could not be attached to it.', 'error');
  }
  // The attach re-derives an instrument's generated_id from its new context, and a line
  // end stores its instrument's label as text, so every line bound to it is respelled.
  const refByInstrumentId = new Map(attachedInstruments.map((instrument) => [instrument.id, refForEntity({ kind: 'instrument', instrument })]));
  const boundLines = [line, ...pnidState.nodes.filter((node) => node.id !== line.id)];
  try {
    for (const boundLine of boundLines) {
      const sourceRef = boundLine.source_id ? refByInstrumentId.get(boundLine.source_id) : undefined;
      const destRef = boundLine.dest_id ? refByInstrumentId.get(boundLine.dest_id) : undefined;
      // A ref written without its id drops the binding, so each ref goes out with its id.
      const respelledEnds = {
        ...(sourceRef !== undefined && sourceRef !== boundLine.source_ref ? { source_ref: sourceRef, source_id: boundLine.source_id } : {}),
        ...(destRef !== undefined && destRef !== boundLine.dest_ref ? { dest_ref: destRef, dest_id: boundLine.dest_id } : {}),
      };
      if (Object.keys(respelledEnds).length) await pnidApi.updateNode(selectedDoc.id, boundLine.id, respelledEnds);
    }
  } catch (error) {
    console.error("Failed to update a line end to its instrument's new label:", error);
    showAlert('Update failed', "The line saved, but a line end still shows its instrument's old label.", 'error');
  }
  // The line is saved and already in the local state, so a failed reload leaves the
  // save complete: the form closes, and the next save of this line edits it.
  try {
    await refreshPnidState(selectedDoc.id);
  } catch (error) {
    console.error('Failed to reload the drawing after the line save:', error);
    showAlert('Refresh failed', 'The line saved, but the drawing could not be reloaded. Reload the page to see the latest.', 'error');
  }
};
